import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispute } from '../../context/DisputeContext';
import { saveAssessment } from '../../utils/api';
import ProgressBar from '../../components/ProgressBar';

// Template-specific questions (max 6)
const QUESTIONS_BY_TYPE = {
  late_payment: [
    {
      id: 'contract_date',
      question: 'When was the contract signed?',
      type: 'date',
      required: true,
    },
    {
      id: 'contract_value',
      question: 'What was the total contract value?',
      type: 'number',
      placeholder: '€',
      required: true,
    },
    {
      id: 'breach_description',
      question: 'Describe the breach in detail',
      type: 'textarea',
      placeholder: 'What specific terms were violated?',
      required: true,
    },
    {
      id: 'amount_claimed',
      question: 'What amount are you claiming?',
      type: 'number',
      placeholder: '€',
      required: true,
    },
    {
      id: 'evidence',
      question: 'What evidence do you have?',
      type: 'textarea',
      placeholder: 'Describe documents, communications, witnesses, etc.',
      required: true,
    },
    {
      id: 'communication_attempts',
      question: 'Have you tried to resolve this directly?',
      type: 'textarea',
      placeholder: 'Describe any previous communication attempts',
      required: false,
    },
  ],
  security_deposit: [
    {
      id: 'incident_date',
      question: 'When did the damage occur?',
      type: 'date',
      required: true,
    },
    {
      id: 'damage_description',
      question: 'Describe the damage',
      type: 'textarea',
      placeholder: 'What was damaged and how?',
      required: true,
    },
    {
      id: 'repair_cost',
      question: 'What is the estimated repair cost?',
      type: 'number',
      placeholder: '€',
      required: true,
    },
    {
      id: 'responsible_party',
      question: 'Who is responsible for the damage?',
      type: 'textarea',
      placeholder: 'Name and relationship to you',
      required: true,
    },
    {
      id: 'evidence',
      question: 'Do you have photos or repair quotes?',
      type: 'textarea',
      placeholder: 'Describe available evidence',
      required: true,
    },
  ],
  personal_loan: [
    {
      id: 'debt_amount',
      question: 'What is the total amount owed?',
      type: 'number',
      placeholder: '€',
      required: true,
    },
    {
      id: 'debt_origin',
      question: 'What is this debt for?',
      type: 'textarea',
      placeholder: 'Invoice, loan, rent, etc.',
      required: true,
    },
    {
      id: 'due_date',
      question: 'When was payment due?',
      type: 'date',
      required: true,
    },
    {
      id: 'payment_terms',
      question: 'What were the payment terms?',
      type: 'textarea',
      placeholder: 'Describe agreed payment schedule',
      required: true,
    },
    {
      id: 'collection_attempts',
      question: 'What collection attempts have you made?',
      type: 'textarea',
      placeholder: 'Reminders, letters, calls, etc.',
      required: true,
    },
  ],
  product_dispute: [
    {
      id: 'purchase_date',
      question: 'When did you purchase the product/service?',
      type: 'date',
      required: true,
    },
    {
      id: 'purchase_amount',
      question: 'How much did you pay?',
      type: 'number',
      placeholder: '€',
      required: true,
    },
    {
      id: 'issue_description',
      question: 'What is wrong with the product/service?',
      type: 'textarea',
      placeholder: 'Describe the defect or problem',
      required: true,
    },
    {
      id: 'resolution_sought',
      question: 'What resolution are you seeking?',
      type: 'select',
      options: ['Full refund', 'Partial refund', 'Replacement', 'Repair', 'Compensation'],
      required: true,
    },
    {
      id: 'seller_contact',
      question: 'Have you contacted the seller?',
      type: 'textarea',
      placeholder: 'Describe their response',
      required: true,
    },
  ],
  neighbor_dispute: [
    {
      id: 'incident_date',
      question: 'When did the dispute start?',
      type: 'date',
      required: true,
    },
    {
      id: 'dispute_type',
      question: 'What type of neighbor dispute is this?',
      type: 'select',
      options: ['Noise complaints', 'Property boundaries', 'Shared costs', 'Tree/vegetation', 'Parking', 'Other'],
      required: true,
    },
    {
      id: 'issue_description',
      question: 'Describe the dispute in detail',
      type: 'textarea',
      placeholder: 'What is the specific problem with your neighbor?',
      required: true,
    },
    {
      id: 'damages_amount',
      question: 'Are you claiming any damages?',
      type: 'number',
      placeholder: '€ (enter 0 if none)',
      required: true,
    },
    {
      id: 'evidence',
      question: 'What evidence do you have?',
      type: 'textarea',
      placeholder: 'Photos, videos, witness statements, documents, etc.',
      required: true,
    },
    {
      id: 'communication_attempts',
      question: 'Have you tried to resolve this with your neighbor?',
      type: 'textarea',
      placeholder: 'Describe any previous conversations or attempts',
      required: false,
    },
  ],
};

const AssessmentPage = () => {
  const navigate = useNavigate();
  const { dispute, updateDispute } = useDispute();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dispute || !dispute.id || !dispute.type) {
      navigate('/start');
    }
  }, [dispute, navigate]);

  if (!dispute || !dispute.type) {
    return null;
  }

  const questions = QUESTIONS_BY_TYPE[dispute.type] || [];

  // If no questions for this type, skip to parameters
  if (questions.length === 0) {
    navigate('/parameters');
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];

  if (!currentQuestion) {
    navigate('/parameters');
    return null;
  }

  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const handleNext = async () => {
    // Validate required fields
    if (currentQuestion.required && !currentAnswer.trim()) {
      setError('This question is required');
      return;
    }

    // Save current answer
    const newAnswers = {
      ...answers,
      [currentQuestion.id]: currentAnswer,
    };
    setAnswers(newAnswers);

    if (isLastQuestion) {
      // Submit all answers
      setSubmitting(true);
      setError(null);

      try {
        const data = await saveAssessment({
          disputeId: dispute.id,
          partyId: dispute.partyAId,
          answers: newAnswers,
        });

        updateDispute(data);
        navigate('/parameters');
      } catch (err) {
        setError(err.message || 'Failed to save assessment. Please try again.');
      } finally {
        setSubmitting(false);
      }
    } else {
      // Move to next question
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer(newAnswers[questions[currentQuestionIndex + 1].id] || '');
      setError(null);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setCurrentAnswer(answers[questions[currentQuestionIndex - 1].id] || '');
      setError(null);
    } else {
      navigate('/classify');
    }
  };

  const renderInput = () => {
    switch (currentQuestion.type) {
      case 'textarea':
        return (
          <textarea
            className="input min-h-[120px]"
            placeholder={currentQuestion.placeholder}
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            disabled={submitting}
            autoFocus
          />
        );

      case 'number':
        return (
          <div className="relative">
            <input
              type="number"
              className="input"
              placeholder={currentQuestion.placeholder}
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            className="input"
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            disabled={submitting}
            autoFocus
          />
        );

      case 'select':
        return (
          <select
            className="input"
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            disabled={submitting}
            autoFocus
          >
            <option value="">Select an option</option>
            {currentQuestion.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            className="input"
            placeholder={currentQuestion.placeholder}
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            disabled={submitting}
            autoFocus
          />
        );
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <ProgressBar current={2} total={5} phase="Assessment" />

      <div className="mb-8 text-center">
        <div className="text-sm text-gray-500 mb-2">
          Question {currentQuestionIndex + 1} of {questions.length}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div
            className="bg-primary-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="card">
        <h2 className="text-2xl font-bold mb-6">{currentQuestion.question}</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="mb-6">
          {renderInput()}
          {!currentQuestion.required && (
            <p className="text-xs text-gray-500 mt-2">This question is optional</p>
          )}
        </div>

        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleBack}
            className="btn-secondary"
            disabled={submitting}
          >
            Back
          </button>

          <button
            type="button"
            onClick={handleNext}
            className="btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : isLastQuestion ? 'Continue to Parameters' : 'Next Question'}
          </button>
        </div>
      </div>

      {/* Help text */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          These questions help our AI understand your case and match it with similar disputes.
          Your answers are private and will only be shared with the other party and supervising attorney.
        </p>
      </div>
    </div>
  );
};

export default AssessmentPage;
